import { AttributeType } from "@energy-advice/easas-api";
import { InputSwitch } from "primereact/inputswitch";
import { useGetProductTemplateConfig } from "api/api-hooks/product-templates-hooks";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import {
  IProductTemplateDropdownChangeEvent,
  ProductTemplateDropdown,
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import LoadConfigInputs, {
  DefaultDatasetLoadingConf,
} from "components/dashboard/charts/LoadConfigInputs";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import React, { useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { aggregationOptions, IProductTemplateBarChart } from "./ProductTemplateBarChartTypes";

const ProductTemplateBarChartEditor: React.FC<DashboardEditorProps<IProductTemplateBarChart>> = ({
  config,
  onConfigChange,
}) => {
  const [currentConfig, setCurrentConfig] = useState<IProductTemplateBarChart>({
    ...config,
    loadConf: config.loadConf ?? DefaultDatasetLoadingConf,
  });

  const { data } = useGetProductTemplateConfig(
    currentConfig.safeName,
    `${currentConfig.id}`,
    [],
    [],
    {
      enabled: !!currentConfig.safeName && !Number.isNaN(Number(currentConfig.id)),
    }
  );

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const safeNameSelectionHandler = (e: IProductTemplateDropdownChangeEvent) => {
    setCurrentConfig(prevState => ({ ...prevState, safeName: e.refName, id: e.value }));
  };

  return (
    <div className="flex flex-column">
      <div className="flex flex-row justify-content-between">
        <div className="w-15rem flex flex-column">
          <label htmlFor="safeName">{T("label.product-template-table")}</label>
          <ProductTemplateDropdown value={currentConfig.id} onChange={safeNameSelectionHandler} />
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex flex-column gap-2">
            <label htmlFor="hideLegend">{T("action.hide-legend")}</label>
            <InputSwitch
              id="hideLegend"
              checked={Boolean(currentConfig.hideLegend)}
              onChange={e => setCurrentConfig(prev => ({ ...prev, hideLegend: e.value }))}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="xAxisTitle">{T("label.x-axis-title")}</label>
            <InputText
              value={currentConfig.xAxisTitle}
              onChange={e => setCurrentConfig(prev => ({ ...prev, xAxisTitle: e.target.value }))}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center">
            <label htmlFor="filter">{T("word.filter")}</label>
            <InputText
              id="filter"
              value={currentConfig?.filter ?? ""}
              onChange={e => setCurrentConfig(prev => ({ ...prev, filter: e.target.value }))}
            />
          </div>
        </div>
      </div>
      <Divider />
      <div className="flex flex-row gap-3">
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="nameColumn">{T("label.name-column")}</label>
          <Dropdown
            value={currentConfig.nameColumn ?? ""}
            options={data?.data?.attributes.filter(attr => attr.type === AttributeType.STRING)}
            optionLabel="displayName"
            optionValue="referenceName"
            placeholder={T("word.select-one")}
            showClear
            id="nameColumn"
            className="p-inputtext-sm w-14rem"
            disabled={!data}
            onChange={e => setCurrentConfig(prev => ({ ...prev, [e.target.id]: e.target.value }))}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="timeColumn">{T("label.time-column")}</label>
          <Dropdown
            value={currentConfig.timeColumn ?? ""}
            options={data?.data?.attributes.filter(attr => attr.type === AttributeType.INSTANT)}
            optionLabel="displayName"
            optionValue="referenceName"
            placeholder={T("word.select-one")}
            showClear
            id="timeColumn"
            disabled={!data}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig(prev => ({ ...prev, [e.target.id]: e.target.value }))}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="valueColumn">{T("label.value-column")}</label>
          <Dropdown
            value={currentConfig.valueColumn ?? ""}
            options={data?.data?.attributes.filter(
              attr =>
                attr.type === AttributeType.DOUBLE ||
                attr.type === AttributeType.LONG ||
                attr.type === AttributeType.TIMESPAN
            )}
            optionLabel="displayName"
            optionValue="referenceName"
            placeholder={T("word.select-one")}
            showClear
            id="valueColumn"
            disabled={!data}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig(prev => ({ ...prev, [e.target.id]: e.target.value }))}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="valueUnit">{T("word.value-unit")}</label>
          <DatasetUnitSelector
            className="w-full"
            value={currentConfig.valueUnit}
            onChange={e => setCurrentConfig(prev => ({ ...prev, valueUnit: e.value }))}
          />
        </div>
      </div>
      <Divider />
      <div className="flex flex-row justify-content-between">
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="aggregationType">{T("label.aggregation-type")}</label>
          <Dropdown
            id="aggregationType"
            value={currentConfig.aggregationType ?? "SUM"}
            options={aggregationOptions}
            placeholder={T("word.select-one")}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig(prev => ({ ...prev, aggregationType: e.value }))}
          />
        </div>
        <div className="gap-2 flex flex-row">
          <div className="gap-2 flex flex-column justify-content-center">
            <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
            <InputNumber
              id="decimalPlaces"
              value={currentConfig.decimalPlaces}
              className="p-inputtext-sm w-14rem"
              onChange={e => setCurrentConfig(prev => ({ ...prev, decimalPlaces: e.value ?? 0 }))}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center">
            <label htmlFor="width">{T("word.width")} (px)</label>
            <InputNumber
              id="width"
              value={currentConfig.width}
              className="p-inputtext-sm w-14rem"
              onChange={e => setCurrentConfig(prev => ({ ...prev, width: e.value ?? 0 }))}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center">
            <label htmlFor="height">{T("word.height")} (px)</label>
            <InputNumber
              id="height"
              value={currentConfig.height}
              className="p-inputtext-sm w-14rem"
              onChange={e => setCurrentConfig(prev => ({ ...prev, height: e.value ?? 0 }))}
            />
          </div>
        </div>
      </div>
      <Divider />
      <div className="flex flex-row justify-content-between">
        <LoadConfigInputs
          loadConf={currentConfig.loadConf}
          handleLoadConfChange={(updatedLoadConf: DatasetLoadingConf) => {
            setCurrentConfig(prev => ({ ...prev, loadConf: updatedLoadConf }));
          }}
        />
      </div>
    </div>
  );
};

export default ProductTemplateBarChartEditor;
